import React, { useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'

import { BRIEF } from '@disco/constants/ItemTypes'
import { openBrief } from '@disco/state-management/actions/ExplorerActions'
import {
  resetSelection,
  selectExplorerItems,
} from '@disco/state-management/actions/SelectionActions'
import {
  getBrief,
  getMovingBriefs,
} from '@disco/state-management/reducers/entities/briefs'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  getExplorerSelection,
  getSelectionIndexes,
} from '@disco/state-management/selectors/selection'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import { handleModifierKeysSelection } from '@disco/utils/selection'
import { formatTimestampShort } from '@disco/utils/time-utils'

import type { Brief, BriefListApiItem } from 'types/brief'
import type { ExplorerPane } from 'types/explorer'
import type { State } from 'types/redux'

import { SelectionContext } from 'contexts/selection'

import { E2Avatar } from 'views/pages/e2/e2.prototype'

import { Box, Tooltip, Flex, Text } from 'components/ds'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Draggable from 'components/ui/draggable'
import { HoverProvider, HoverContext } from 'components/ui/hover-provider'
import Lozenge from 'components/ui/lozenge'
import MediaItem from 'components/ui/media-item'
import PaneItem from 'components/ui/pane-item'

import { useDispatch } from 'hooks/use-redux'

import BriefStatusBadge from 'features/briefs/components/brief-status'
import BriefProjectSettingsDialog from 'features/briefs/components/brief-project-settings-dialog'
import Icon from 'components/ui/icon'
import briefProjectStyles from '../brief-project/index.module.css'
import BriefActionMenu from 'features/briefs/components/brief-action-menu'

type Props = {
  brief: BriefListApiItem
  index: number
  appearance: 'default' | 'active' | 'selected'
  pane: ExplorerPane
  itemPrefix?: string
  isSearchResult: boolean
}

const BriefActions = ({ brief }: { brief: BriefListApiItem }) => {
  const isPhone = useSelector(isDeviceTypePhone)
  const constanceSettings = useSelector(getConstanceSettings)
  const briefWorkflowV2Enabled = constanceSettings?.BRIEF_WORKFLOW_V2 || false
  const hoverContext = useContext(HoverContext)
  const briefState = useSelector(state =>
    getBrief(state, { briefId: brief.id }),
  )

  return (
    <ButtonContainer>
      {!isPhone && (
        <>
          <Tooltip content="Edit brief">
            <Button
              appearance="icon"
              compact
              icon="edit"
              link={
                briefWorkflowV2Enabled
                  ? `/brief-editor-v2/${brief.id}`
                  : `/brief-editor/${brief.id}/content`
              }
            />
          </Tooltip>
          {briefState && (
            <BriefActionMenu
              brief={briefState}
              hoverContext={hoverContext}
              iconColor="black"
            />
          )}
        </>
      )}
    </ButtonContainer>
  )
}

const BriefItem = ({
  brief,
  index,
  appearance,
  pane,
  isSearchResult,
}: Props) => {
  const dispatch = useDispatch()
  const selectionContext = useContext(SelectionContext)
  const explorerSelection = useSelector(getExplorerSelection)
  const selection: Record<string, any> = useSelector((state: State) =>
    getSelectionIndexes(state, selectionContext.id),
  )
  const isPartOfMultipleSelection: boolean = selection.includes(index)
  const movingBriefIds = useSelector(getMovingBriefs)

  const handleBriefClick = useCallback(
    (event: MouseEvent) => {
      // handle selection actions
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        handleModifierKeysSelection(event, {
          selection: explorerSelection,
          index,
          selectionId: selectionContext.id,
          pane,
          selectItems: (_pane, selectionRange, selectionType) =>
            dispatch(
              selectExplorerItems(
                selectionContext.id,
                selectionRange,
                selectionType,
              ),
            ),
          excludeItemTypes: ['brief_project'],
        })
        return
      }

      if (pane && typeof brief.inbox === 'number') {
        dispatch(openBrief(pane, index, brief))
      }
    },
    [dispatch, pane, brief, index, explorerSelection, selectionContext.id],
  )

  const handleDragStart = useCallback(() => {
    dispatch(resetSelection())
  }, [dispatch])

  const showProjectInBriefItem = isSearchResult && brief.project?.name

  const caption = (
    <Flex direction="column">
      <Flex mt="1" gap="1">
        <BriefStatusBadge
          submissionStatus={brief.submission_status}
          publishingStatus={brief.publishing_status}
        />
        <E2Avatar
          firstName={brief.created_by.first_name}
          lastName={brief.created_by.last_name}
          email={brief.created_by.email}
        />
        {formatTimestampShort(brief.created)}
      </Flex>
      {showProjectInBriefItem && (
        <Flex mt="2">
          <Box p="1" mr="1" className={briefProjectStyles.artwork}>
            <Icon type="folder" height={12} width={12} />
          </Box>
          <Text weight="medium" color="gray" highContrast>
            {brief.project?.name}
          </Text>
        </Flex>
      )}
    </Flex>
  )

  return (
    <Draggable
      id={`${pane.index}-brief-${brief.id}`}
      data={{
        isPartOfMultipleSelection,
        selection,
        brief,
      }}
      type={BRIEF}
      disabled={isSearchResult || movingBriefIds.includes(brief.id)}
      onDragStart={isPartOfMultipleSelection ? undefined : handleDragStart}
    >
      <HoverProvider>
        <PaneItem
          appearance={appearance}
          onClick={handleBriefClick}
          content={<MediaItem title={brief.name} caption={caption} />}
          nodeRight={<Lozenge color="gray" text={brief.response_count} />}
          actions={<BriefActions brief={brief} />}
          className={showProjectInBriefItem && 'component-height--Tall'}
        />
      </HoverProvider>
    </Draggable>
  )
}

export default React.memo<Props>(BriefItem)
